/*
 * Sticky Add-to-Cart バー（PDP）
 *
 * 表示制御: メイン CTA（#ProductSubmitButton-<section>）を IntersectionObserver で監視し、
 *   viewport 外にある間だけバーを表示する。上下どちらに外れても表示する（初期表示で
 *   CTA がフォールド下にあるケースを救うのが本命のため、スクロール方向は問わない）。
 * 状態同期: メイン CTA の disabled / 文言 / loading を MutationObserver でミラーする。
 *   product-form.js（Rise 本体）を改変せずに、在庫切れ・組み合わせ不可・カート追加中の
 *   状態変化へ追従するための片方向ミラー。
 * 選択サマリ: variant-selects 内の change を拾い、各オプションの選択値を独立 span で描画。
 *   長いカラーウェイ名でサイズが省略され消えるのを防ぐため、サイズ判定したオプションには
 *   省略禁止のモディファイア（--size）を付与する（判定は Variant ピッカーと同じ
 *   「オプション名にサイズ/size を含む」ゼロコンフィグ検出。CSS 側で flex-shrink: 0）。
 * 価格: #price-<section> のサブツリー変化（variant 切替時の Section Rendering 再描画）を
 *   監視し、表示中の価格テキスト（セール時はセール価格）を転写する。
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    // buy_buttons ブロックが無いテンプレート構成では何もしない（バーは非表示のまま）
    if (!this.mainSubmit) return;

    this.submitButton = this.querySelector('[data-sticky-atc-submit]');
    this.submitLabel = this.querySelector('[data-sticky-atc-submit-label]');
    this.optionsEl = this.querySelector('[data-sticky-atc-options]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');

    this.visibilityObserver = new IntersectionObserver((entries) => {
      this.classList.toggle('sticky-atc--visible', !entries[0].isIntersecting);
    });
    this.visibilityObserver.observe(this.mainSubmit);

    this.mainMirror = new MutationObserver(() => this.syncFromMain());
    this.mainMirror.observe(this.mainSubmit, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.syncFromMain();

    this.priceSource = document.getElementById(`price-${this.sectionId}`);
    if (this.priceSource) {
      this.priceMirror = new MutationObserver(() => this.syncPrice());
      this.priceMirror.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      this.syncPrice();
    }

    this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
    if (this.variantSelects) {
      this.onVariantChange = () => this.syncOptions();
      this.variantSelects.addEventListener('change', this.onVariantChange);
      this.syncOptions();
    }
  }

  disconnectedCallback() {
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.mainMirror) this.mainMirror.disconnect();
    if (this.priceMirror) this.priceMirror.disconnect();
    if (this.variantSelects && this.onVariantChange) {
      this.variantSelects.removeEventListener('change', this.onVariantChange);
    }
  }

  syncFromMain() {
    if (!this.submitButton) return;
    this.submitButton.toggleAttribute('disabled', this.mainSubmit.hasAttribute('disabled'));
    this.submitButton.classList.toggle('loading', this.mainSubmit.classList.contains('loading'));
    // メイン CTA の文言 span（先頭の子 span）だけを転写する。
    // スピナー SVG や pickup availability 等の付随ノードを巻き込まないための限定コピー。
    const label = this.mainSubmit.querySelector('span');
    if (label && this.submitLabel) {
      const text = label.textContent.trim();
      if (text && this.submitLabel.textContent !== text) this.submitLabel.textContent = text;
    }
  }

  syncPrice() {
    if (!this.priceEl || !this.priceSource) return;
    const container = this.priceSource.querySelector('.price__container') || this.priceSource;
    const item =
      container.querySelector('.price__sale .price-item--sale') ||
      container.querySelector('.price-item--sale') ||
      container.querySelector('.price-item--regular');
    if (item) {
      const text = item.textContent.trim();
      if (text) this.priceEl.textContent = text;
    }
  }

  syncOptions() {
    if (!this.optionsEl || !this.variantSelects) return;
    const entries = [];
    const isSizeName = (text) => /サイズ|size/i.test(text || '');
    this.variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const legend = fieldset.querySelector('legend');
      entries.push({ value: checked.value, isSize: isSizeName(legend && legend.textContent) });
    });
    this.variantSelects.querySelectorAll('select').forEach((select) => {
      if (!select.value) return;
      const input = select.closest('.product-form__input');
      const label = input ? input.querySelector('.form__label') : null;
      entries.push({ value: select.value, isSize: isSizeName(label && label.textContent) });
    });
    this.optionsEl.textContent = '';
    entries.forEach((entry) => {
      const span = document.createElement('span');
      span.className = entry.isSize ? 'sticky-atc__option sticky-atc__option--size' : 'sticky-atc__option';
      span.textContent = entry.value;
      this.optionsEl.appendChild(span);
    });
  }
}

customElements.define('sticky-atc', StickyAtc);
